import { getCurrentOperatorName } from '../../utils/currentOperator';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import BaseTicketScreen from './BaseTicketScreen';
import { FormRow, FormGroup, Label, Input, Select, FieldError } from './shared/TicketStyledComponents';
import {
  SwapLegsContainer, SwapLegPanel, SwapLegHeader, SwapLegTitle, SwapLegIcon,
  SwapArrowDivider, SwapArrow, LegFormRow, LegFormRowSingle, SwapDirectionToggle, DirectionOption
} from './shared/SwapLegStyledComponents';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';
import { provisionalSettleDate, resolveSettleDate, settlementCalendars, SettlementQuery } from '../../utils/settlement';
import { formatAmountWithCommas, parseAmountShorthand } from '../../utils/amountUtils';
import axios from 'axios';

import { DateField } from '../shared/DateField';

// ADR 0009 §4.2: a swap's dates run on its currency's calendar, not the book's. The ticket shows
// the settle date the server will store; the browser's count stands in until the server answers.
const settleQuery = (tradeDate: string, currency?: string): SettlementQuery =>
  ({ tradeDate, ticketType: 'TRS', convention: 'T+1', currency: currency || 'USD' });

/** Applies the server's settle date if the form still holds the inputs and the date it was asked about. */
const applyServerSettleDate = (setFormData: any, query: SettlementQuery, expected: string) => {
  resolveSettleDate(query).then(settleDate => setFormData((prev: any) =>
    prev.tradeDate === query.tradeDate && (prev.trCurrency || 'USD') === query.currency
      && prev.settleDate === expected && settleDate && settleDate !== expected
      ? { ...prev, settleDate } : prev));
};

/** After a trade date or currency change: the browser's count at once, then the server's. */
const refreshSettleDate = (setFormData: any, query: SettlementQuery) => {
  if (!query.tradeDate) return;
  const provisional = provisionalSettleDate(query);
  setFormData((prev: any) => ({ ...prev, settleDate: provisional }));
  applyServerSettleDate(setFormData, query, provisional);
};

const DerivativesTRSwapTicketScreen: React.FC = () => {
  const location = useLocation();

  const getPendingTradeData = () => {
    try {
      const pendingData = sessionStorage.getItem('pendingTradeData');
      if (pendingData) {
        const parsed = JSON.parse(pendingData);
        sessionStorage.removeItem('pendingTradeData');
        return parsed;
      }
    } catch (error) {
      console.error('Error reading pending trade data:', error);
    }
    return null;
  };

  const pendingTrade = getPendingTradeData();

  const getInitialFormData = () => {
    const hedgeData = location.state?.hedgeData;
    const portfolioId = location.state?.portfolioId;
    const tradeDate = pendingTrade?.tradeDate || getTodayDate(calendarsFor('TRS', pendingTrade?.trCurrency));

    const baseFormData = {
      // Direction: who receives total return
      direction: (pendingTrade?.direction || 'RECEIVE_TOTAL_RETURN') as string,
      assetManager: pendingTrade?.assetManager || '',
      portfolioManager: pendingTrade?.portfolioManager || '',
      assetClass: 'Derivatives',
      assetSubClass: 'Total Return Swaps',
      security: pendingTrade?.securityName || pendingTrade?.security || '',
      // Common
      tradeDate,
      settleDate: pendingTrade?.settleDate || provisionalSettleDate(settleQuery(tradeDate, pendingTrade?.trCurrency)),
      effectiveDate: pendingTrade?.effectiveDate || '',
      maturityDate: pendingTrade?.maturityDate || '',
      tenor: pendingTrade?.tenor || '',
      portfolio: pendingTrade?.portfolioName || pendingTrade?.portfolio || '',
      submittedBy: pendingTrade?.submittedBy || getCurrentOperatorName(),
      executedBy: pendingTrade?.executedBy || '',
      notes: pendingTrade?.notes || '',
      // Total Return leg
      trReferenceAsset: pendingTrade?.referenceAsset || '',
      trReferenceType: pendingTrade?.referenceType || 'SINGLE_ASSET',
      trCurrency: pendingTrade?.trCurrency || 'USD',
      trNotional: pendingTrade?.trNotional?.toString() || '',
      trInitialPrice: pendingTrade?.initialPrice?.toString() || '',
      trPayFrequency: pendingTrade?.trPayFrequency || 'Quarterly',
      trDayCount: pendingTrade?.trDayCount || 'ACT/360',
      // Financing leg
      finCurrency: pendingTrade?.finCurrency || 'USD',
      finNotional: pendingTrade?.finNotional?.toString() || '',
      finIndex: pendingTrade?.finIndex || 'SOFR',
      finIndexRate: pendingTrade?.finIndexRate?.toString() || '',
      finSpread: pendingTrade?.finSpread?.toString() || '',
      finPayFrequency: pendingTrade?.finPayFrequency || 'Quarterly',
      finResetFrequency: pendingTrade?.finResetFrequency || 'Quarterly',
      finDayCount: pendingTrade?.finDayCount || 'ACT/360',
    };

    if (hedgeData) {
      if (hedgeData.securityName) baseFormData.security = hedgeData.securityName;
      if (hedgeData.identifier) baseFormData.trReferenceAsset = hedgeData.identifier;
      if (hedgeData.notionalAmount) {
        const notional = typeof hedgeData.notionalAmount === 'number'
          ? hedgeData.notionalAmount : parseFloat(hedgeData.notionalAmount);
        if (!isNaN(notional)) {
          const formatted = notional.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          baseFormData.trNotional = formatted;
          baseFormData.finNotional = formatted;
        }
      }
      if (portfolioId) baseFormData.portfolio = String(portfolioId);
    }

    return baseFormData;
  };

  const initialFormData = getInitialFormData();

  const validateForm = (formData: any, action: string): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};
    if (!formData.assetManager?.trim()) errors.assetManager = 'Asset Manager is required';
    if (!formData.portfolioManager?.trim()) errors.portfolioManager = 'Portfolio Manager is required';
    if (!formData.trReferenceAsset?.trim()) errors.trReferenceAsset = 'Reference Asset is required';
    if (!formData.trNotional?.trim()) errors.trNotional = 'Notional Amount is required';
    if (!formData.trInitialPrice?.trim()) errors.trInitialPrice = 'Initial Price is required (notional = face × price / 100)';
    if (!formData.finIndex?.trim()) errors.finIndex = 'Financing Index is required';
    if (!formData.finIndexRate?.trim()) errors.finIndexRate = 'Index Fixing (%) is required — no rates feed; accruals use this';
    if (!formData.finSpread?.trim()) errors.finSpread = 'Financing Spread is required';
    if (!formData.tenor?.trim() && !formData.maturityDate) errors.tenor = 'Tenor or Maturity Date is required';
    if (!formData.portfolio?.trim()) errors.portfolio = 'Portfolio is required';
    if (!formData.tradeDate) errors.tradeDate = 'Trade Date is required';
    if (!formData.settleDate) errors.settleDate = 'Settle Date is required';
    if (action === 'execute') {
      if (!formData.executedBy?.trim()) errors.executedBy = 'Executed By is required for execution';
    }
    return errors;
  };

  // TRS E2E rewire (2026-08-06, IRS/CDS template): the ticket's terms ARE
  // the bespoke contract — /prepare mints dim_trs_contract + a classified
  // per-contract SMF (TS-prefix 9-char CUSIP) and returns the sourceRef the
  // posting SPI resolves at POST. An at-market TRS is unfunded synthetic
  // exposure: the ticket declares netAmount = 0 (zero inception cash).
  const buildTransactionData = async (formData: any, dimensionData: any): Promise<any> => {
    const portfolio = dimensionData.portfolios.find((p: any) => p.portfolioName === formData.portfolio.trim());
    if (!portfolio) throw new Error(`Portfolio "${formData.portfolio}" not found`);

    const notional = parseAmountShorthand(formData.trNotional);
    const prepareRes = await axios.post('/api/derivatives/trs/prepare', {
      portfolioId: portfolio.id,
      trsType: formData.trReferenceType || 'SINGLE_ASSET',
      direction: formData.direction,
      notional,
      currency: formData.trCurrency || 'USD',
      referenceAssetCusip: formData.trReferenceAsset || null,
      referenceAssetDescription: formData.security || null,
      initialPrice: formData.trInitialPrice ? parseFloat(formData.trInitialPrice) : null,
      financingRateIndex: formData.finIndex || null,
      financingIndexRate: formData.finIndexRate ? parseFloat(formData.finIndexRate) : null,
      financingSpreadBps: formData.finSpread ? parseFloat(formData.finSpread) : null,
      paymentFrequency: formData.trPayFrequency || null,
      resetFrequency: formData.finResetFrequency || null,
      tradeDate: formData.tradeDate,
      effectiveDate: formData.effectiveDate || formData.settleDate || null,
      maturityDate: formData.maturityDate || null,
      tenor: formData.tenor || null,
    });
    const { securityId, sourceRef } = prepareRes.data;

    return {
      transactionType: formData.direction === 'RECEIVE_TOTAL_RETURN' ? 'BUY' : 'SELL',
      securityId,
      portfolioId: portfolio.id,
      quantity: notional,
      price: 100,
      amount: notional,
      netAmount: 0,
      tradeDate: formData.tradeDate,
      settleDate: formData.settleDate,
      notes: formData.notes || `TRS ${formData.direction === 'RECEIVE_TOTAL_RETURN' ? 'Receive' : 'Pay'} TR ${formData.trReferenceAsset} vs ${formData.finIndex}+${formData.finSpread}bps`,
      createdBy: formData.submittedBy,
      executedBy: formData.executedBy || null,
      status: 'DRAFT',
      ticketType: 'TRS',
      eventType: 'ACQUIRE',
      sourceRef,
    };
  };

  // Reopen/edit: contract terms live on dim_trs_contract (resolved by
  // sourceRef), not as flat trade fields.
  const loadTransactionData = async (transaction: any) => {
    const tradeDate = transaction.tradeDate || getTodayDate(calendarsFor('TRS', transaction.currency));
    const base = {
      direction: transaction.transactionType === 'SELL' ? 'PAY_TOTAL_RETURN' : 'RECEIVE_TOTAL_RETURN',
      assetManager: transaction.assetManagerName || transaction.assetManager || '',
      portfolioManager: transaction.portfolioManagerName || transaction.portfolioManager || '',
      assetClass: 'Derivatives',
      assetSubClass: 'Total Return Swaps',
      security: transaction.securityName || transaction.security || '',
      tradeDate,
      settleDate: transaction.settleDate || provisionalSettleDate(settleQuery(tradeDate, transaction.currency)),
      effectiveDate: '',
      maturityDate: '',
      tenor: '',
      portfolio: transaction.portfolioName || transaction.portfolio || '',
      submittedBy: transaction.createdBy || getCurrentOperatorName(),
      executedBy: transaction.executedBy || '',
      notes: transaction.notes || '',
      trReferenceAsset: '',
      trReferenceType: 'SINGLE_ASSET',
      trCurrency: transaction.currency || 'USD',
      trNotional: transaction.amount?.toString() || '',
      trInitialPrice: '',
      trPayFrequency: 'Quarterly',
      trDayCount: 'ACT/360',
      finCurrency: transaction.currency || 'USD',
      finNotional: transaction.amount?.toString() || '',
      finIndex: 'SOFR',
      finIndexRate: '',
      finSpread: '',
      finPayFrequency: 'Quarterly',
      finResetFrequency: 'Quarterly',
      finDayCount: 'ACT/360',
    };
    const m = (transaction.sourceRef || '').match(/trs-contract:(\d+)/);
    if (!m) return base;
    try {
      const c = (await axios.get(`/api/derivatives/trs/contract/${m[1]}`)).data;
      return {
        ...base,
        direction: c.totalReturnPayerReceiver === 'PAYER' ? 'PAY_TOTAL_RETURN' : 'RECEIVE_TOTAL_RETURN',
        effectiveDate: c.effectiveDate || '',
        maturityDate: c.maturityDate || '',
        trReferenceAsset: c.referenceAssetCusip || '',
        trReferenceType: c.trsType || 'SINGLE_ASSET',
        trCurrency: c.currency || base.trCurrency,
        finCurrency: c.currency || base.finCurrency,
        trNotional: c.notionalAmount != null ? String(c.notionalAmount) : base.trNotional,
        finNotional: c.notionalAmount != null ? String(c.notionalAmount) : base.finNotional,
        trInitialPrice: c.initialPrice != null ? String(parseFloat(c.initialPrice)) : '',
        trPayFrequency: c.paymentFrequency || base.trPayFrequency,
        finIndex: c.financingRateIndex || base.finIndex,
        finIndexRate: c.financingIndexRate != null ? String(parseFloat(c.financingIndexRate)) : '',
        finSpread: c.financingSpreadBps != null ? String(parseFloat(c.financingSpreadBps)) : '',
        finResetFrequency: c.resetFrequency || base.finResetFrequency,
      };
    } catch { return base; }
  };

  return (
    <BaseTicketScreen
      title="Total Return Swap Trade Entry"
      assetClass="Derivatives"
      assetSubClass="Total Return Swaps"
      initialFormData={initialFormData}
      validateForm={validateForm}
      buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
    >
      {({ formData, setFormData, errors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange, handleInputBlur, editingTransactionId }) => (
        <TRSwapFormContent
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          dimensionData={dimensionData}
          transactionStatus={transactionStatus}
          isFieldDisabled={isFieldDisabled}
          handleInputChange={handleInputChange}
          handleInputBlur={handleInputBlur}
          location={location}
          editingTransactionId={editingTransactionId}
        />
      )}
    </BaseTicketScreen>
  );
};

const TRSwapFormContent: React.FC<{
  formData: any;
  setFormData: any;
  errors: any;
  dimensionData: any;
  transactionStatus: any;
  isFieldDisabled: any;
  handleInputChange: any;
  handleInputBlur: any;
  location: any;
  editingTransactionId: number | null;
}> = ({ formData, setFormData, errors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange, handleInputBlur, location, editingTransactionId }) => {

  useEffect(() => {
    const portfolioId = location.state?.portfolioId;
    const portfolioName = location.state?.portfolioName;
    const currentPortfolio = formData.portfolio;
    const isPortfolioId = currentPortfolio && /^\d+$/.test(String(currentPortfolio).trim());
    if (portfolioName && (!currentPortfolio || isPortfolioId)) {
      setFormData((prev: any) => ({ ...prev, portfolio: portfolioName }));
    } else if (portfolioId && dimensionData.portfolios.length > 0 && (!currentPortfolio || isPortfolioId)) {
      const portfolio = dimensionData.portfolios.find((p: any) => String(p.id) === String(portfolioId));
      if (portfolio?.portfolioName) {
        setFormData((prev: any) => ({ ...prev, portfolio: portfolio.portfolioName }));
      }
    }
  }, [dimensionData.portfolios, location.state?.portfolioId, location.state?.portfolioName, formData.portfolio, setFormData]);

  // A new ticket asks the server for its settle date on open; a reopened trade keeps its own.
  useEffect(() => {
    if (editingTransactionId || location.state?.transactionId || location.state?.id || !formData.tradeDate) return;
    applyServerSettleDate(setFormData, settleQuery(formData.tradeDate, formData.trCurrency), formData.settleDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNotionalChange = (leg: 'tr' | 'fin', value: string) => {
    const key = leg === 'tr' ? 'trNotional' : 'finNotional';
    const otherKey = leg === 'tr' ? 'finNotional' : 'trNotional';
    setFormData((prev: any) => ({ ...prev, [key]: value, [otherKey]: value }));
  };

  const handleNotionalBlur = (value: string) => {
    const parsed = parseAmountShorthand(value);
    if (parsed > 0) {
      const formatted = formatAmountWithCommas(parsed);
      setFormData((prev: any) => ({ ...prev, trNotional: formatted, finNotional: formatted }));
    }
  };

  const isReceiveTR = formData.direction === 'RECEIVE_TOTAL_RETURN';

  return (
    <>
      {/* ── Header Row: Direction, Trade Date, Settle Date ── */}
      <FormRow>
        <FormGroup>
          <Label>Direction*</Label>
          <SwapDirectionToggle>
            <DirectionOption
              variant="receive"
              active={isReceiveTR}
              disabled={isFieldDisabled('direction')}
              onClick={() => handleInputChange('direction', 'RECEIVE_TOTAL_RETURN')}
            >
              Receive Total Return
            </DirectionOption>
            <DirectionOption
              variant="pay"
              active={!isReceiveTR}
              disabled={isFieldDisabled('direction')}
              onClick={() => handleInputChange('direction', 'PAY_TOTAL_RETURN')}
            >
              Pay Total Return
            </DirectionOption>
          </SwapDirectionToggle>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="tradeDate">Trade Date*</Label>
          <DateField
            id="tradeDate"
            value={formData.tradeDate ?? ''}
            className={errors.tradeDate ? 'error' : ''}
            disabled={isFieldDisabled('tradeDate')}
            calendars={calendarsFor('TRS', formData.trCurrency)}
            onChange={iso => {
              handleInputChange('tradeDate', iso);
              refreshSettleDate(setFormData, settleQuery(iso, formData.trCurrency));
            }}
          />
          {errors.tradeDate && <FieldError>{errors.tradeDate}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="settleDate">Settle Date*</Label>
          <DateField
            id="settleDate"
            value={formData.settleDate ?? ''}
            className={errors.settleDate ? 'error' : ''}
            disabled={isFieldDisabled('settleDate')}
            calendars={settlementCalendars({ ticketType: 'TRS', currency: formData.trCurrency })}
            onChange={iso => handleInputChange('settleDate', iso)}
          />
          {errors.settleDate && <FieldError>{errors.settleDate}</FieldError>}
        </FormGroup>
      </FormRow>

      {/* ── Row 2: Asset Manager, PM, Executed By ── */}
      <FormRow>
        <FormGroup>
          <Label htmlFor="assetManager">Asset Manager*</Label>
          <Select
            id="assetManager"
            value={formData.assetManager}
            onChange={(e) => handleInputChange('assetManager', e.target.value)}
            className={errors.assetManager ? 'error' : ''}
            disabled={isFieldDisabled('assetManager')}
          >
            <option value="">Select Asset Manager</option>
            {dimensionData.assetManagers.map((am: any) => (
              <option key={am.id} value={am.assetManagerName}>{am.assetManagerName}</option>
            ))}
          </Select>
          {errors.assetManager && <FieldError>{errors.assetManager}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="portfolioManager">Portfolio Manager*</Label>
          <Select
            id="portfolioManager"
            value={formData.portfolioManager}
            onChange={(e) => handleInputChange('portfolioManager', e.target.value)}
            className={errors.portfolioManager ? 'error' : ''}
            disabled={isFieldDisabled('portfolioManager')}
          >
            <option value="">Select Portfolio Manager</option>
            {dimensionData.portfolioManagers.map((pm: any) => (
              <option key={pm.id} value={pm.portfolioManagerName}>{pm.portfolioManagerName}</option>
            ))}
          </Select>
          {errors.portfolioManager && <FieldError>{errors.portfolioManager}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="executedBy">Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
          <Select
            id="executedBy"
            value={formData.executedBy}
            onChange={(e) => handleInputChange('executedBy', e.target.value)}
            disabled={isFieldDisabled('executedBy')}
            className={errors.executedBy ? 'error' : ''}
          >
            <option value="">Select</option>
            {dimensionData.users.map((user: any) => (
              <option key={user.id} value={user.fullName}>{user.fullName} {user.role ? `(${user.role})` : ''}</option>
            ))}
          </Select>
          {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
        </FormGroup>
      </FormRow>

      {/* ── Row 3: Contract Name, Identifier, Tenor ── */}
      <FormRow>
        <FormGroup>
          <Label htmlFor="security">Contract Name</Label>
          <Input
            id="security"
            type="text"
            value={formData.security}
            onChange={(e) => handleInputChange('security', e.target.value)}
            placeholder="e.g., AAPL 1Y TRS"
            disabled={isFieldDisabled('security')}
          />
        </FormGroup>

        {/* Identifier field REMOVED (TRS E2E 2026-08-06): the contract +
            classified SMF mint from the ticket's terms at /prepare. */}
        <FormGroup>
          <Label htmlFor="tenor">Tenor*</Label>
          <Input
            id="tenor"
            type="text"
            value={formData.tenor}
            onChange={(e) => handleInputChange('tenor', e.target.value)}
            placeholder="e.g., 1Y, 3Y"
            className={errors.tenor ? 'error' : ''}
            disabled={isFieldDisabled('tenor')}
          />
          {errors.tenor && <FieldError>{errors.tenor}</FieldError>}
        </FormGroup>
      </FormRow>

      {/* ── Row 4: Effective Date, Maturity Date ── */}
      <FormRow>
        <FormGroup>
          <Label htmlFor="effectiveDate">Effective Date</Label>
          <DateField
            id="effectiveDate"
            value={formData.effectiveDate ?? ''}
            disabled={isFieldDisabled('effectiveDate')}
            onChange={iso => handleInputChange('effectiveDate', iso)}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="maturityDate">Maturity Date</Label>
          <DateField
            id="maturityDate"
            value={formData.maturityDate ?? ''}
            disabled={isFieldDisabled('maturityDate')}
            onChange={iso => handleInputChange('maturityDate', iso)}
          />
        </FormGroup>

        <FormGroup />
      </FormRow>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/*  TWO-PANEL SWAP LEGS                                               */}
      {/* ════════════════════════════════════════════════════════════════════ */}

      <SwapLegsContainer>
        {/* ── TOTAL RETURN LEG ── */}
        <SwapLegPanel variant={isReceiveTR ? 'receive' : 'pay'}>
          <SwapLegHeader variant={isReceiveTR ? 'receive' : 'pay'}>
            <SwapLegIcon variant={isReceiveTR ? 'receive' : 'pay'}>
              {isReceiveTR ? '↓' : '↑'}
            </SwapLegIcon>
            <SwapLegTitle variant={isReceiveTR ? 'receive' : 'pay'}>
              {isReceiveTR ? 'Receive' : 'Pay'} Total Return
            </SwapLegTitle>
          </SwapLegHeader>

          <LegFormRowSingle>
            <FormGroup>
              <Label htmlFor="trReferenceAsset">Reference Asset (CUSIP/ISIN)*</Label>
              <Input
                id="trReferenceAsset"
                type="text"
                value={formData.trReferenceAsset}
                onChange={(e) => handleInputChange('trReferenceAsset', e.target.value)}
                placeholder="e.g., 037833100 (AAPL)"
                className={errors.trReferenceAsset ? 'error' : ''}
                disabled={isFieldDisabled('trReferenceAsset')}
              />
              {errors.trReferenceAsset && <FieldError>{errors.trReferenceAsset}</FieldError>}
            </FormGroup>
          </LegFormRowSingle>

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="trReferenceType">Reference Type</Label>
              <Select
                id="trReferenceType"
                value={formData.trReferenceType}
                onChange={(e) => handleInputChange('trReferenceType', e.target.value)}
                disabled={isFieldDisabled('trReferenceType')}
              >
                <option value="SINGLE_ASSET">Single Asset</option>
                <option value="BASKET">Basket</option>
                <option value="INDEX">Index</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="trCurrency">Currency*</Label>
              <Select
                id="trCurrency"
                value={formData.trCurrency}
                onChange={(e) => {
                  handleInputChange('trCurrency', e.target.value);
                  refreshSettleDate(setFormData, settleQuery(formData.tradeDate, e.target.value));
                }}
                disabled={isFieldDisabled('trCurrency')}
              >
                {dimensionData.currencies.map((c: any) => (
                  <option key={c.id} value={c.currencyCode}>{c.currencyCode}</option>
                ))}
              </Select>
            </FormGroup>
          </LegFormRow>

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="trNotional">Notional Amount*</Label>
              <Input
                id="trNotional"
                type="text"
                value={formData.trNotional}
                onChange={(e) => handleNotionalChange('tr', e.target.value)}
                onBlur={(e) => handleNotionalBlur(e.target.value)}
                placeholder="e.g., 25MM"
                className={errors.trNotional ? 'error' : ''}
                disabled={isFieldDisabled('trNotional')}
              />
              {errors.trNotional && <FieldError>{errors.trNotional}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="trInitialPrice">Initial Price</Label>
              <Input
                id="trInitialPrice"
                type="number"
                step="0.01"
                value={formData.trInitialPrice}
                onChange={(e) => handleInputChange('trInitialPrice', e.target.value)}
                placeholder="e.g., 150.25"
                disabled={isFieldDisabled('trInitialPrice')}
              />
            </FormGroup>
          </LegFormRow>

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="trPayFrequency">Payment Frequency</Label>
              <Select
                id="trPayFrequency"
                value={formData.trPayFrequency}
                onChange={(e) => handleInputChange('trPayFrequency', e.target.value)}
                disabled={isFieldDisabled('trPayFrequency')}
              >
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Semi-Annual">Semi-Annual</option>
                <option value="Annual">Annual</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="trDayCount">Day Count</Label>
              <Select
                id="trDayCount"
                value={formData.trDayCount}
                onChange={(e) => handleInputChange('trDayCount', e.target.value)}
                disabled={isFieldDisabled('trDayCount')}
              >
                <option value="ACT/360">ACT/360</option>
                <option value="ACT/365">ACT/365</option>
                <option value="ACT/ACT">ACT/ACT</option>
                <option value="30/360">30/360</option>
              </Select>
            </FormGroup>
          </LegFormRow>
        </SwapLegPanel>

        {/* ── ARROW DIVIDER ── */}
        <SwapArrowDivider>
          <SwapArrow>⇄</SwapArrow>
        </SwapArrowDivider>

        {/* ── FINANCING LEG ── */}
        <SwapLegPanel variant={isReceiveTR ? 'pay' : 'receive'}>
          <SwapLegHeader variant={isReceiveTR ? 'pay' : 'receive'}>
            <SwapLegIcon variant={isReceiveTR ? 'pay' : 'receive'}>
              {isReceiveTR ? '↑' : '↓'}
            </SwapLegIcon>
            <SwapLegTitle variant={isReceiveTR ? 'pay' : 'receive'}>
              {isReceiveTR ? 'Pay' : 'Receive'} Financing
            </SwapLegTitle>
          </SwapLegHeader>

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="finCurrency">Currency*</Label>
              <Select
                id="finCurrency"
                value={formData.finCurrency}
                onChange={(e) => handleInputChange('finCurrency', e.target.value)}
                disabled={isFieldDisabled('finCurrency')}
              >
                {dimensionData.currencies.map((c: any) => (
                  <option key={c.id} value={c.currencyCode}>{c.currencyCode}</option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="finNotional">Notional Amount*</Label>
              <Input
                id="finNotional"
                type="text"
                value={formData.finNotional}
                onChange={(e) => handleNotionalChange('fin', e.target.value)}
                onBlur={(e) => handleNotionalBlur(e.target.value)}
                placeholder="e.g., 25MM"
                disabled={isFieldDisabled('finNotional')}
              />
            </FormGroup>
          </LegFormRow>

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="finIndex">Rate Index*</Label>
              <Select
                id="finIndex"
                value={formData.finIndex}
                onChange={(e) => handleInputChange('finIndex', e.target.value)}
                className={errors.finIndex ? 'error' : ''}
                disabled={isFieldDisabled('finIndex')}
              >
                <option value="">Select</option>
                <option value="SOFR">SOFR</option>
                <option value="EURIBOR">EURIBOR</option>
                <option value="SONIA">SONIA</option>
                <option value="TONAR">TONAR</option>
              </Select>
              {errors.finIndex && <FieldError>{errors.finIndex}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="finSpread">Spread (bps)*</Label>
              <Input
                id="finSpread"
                type="number"
                step="0.01"
                value={formData.finSpread}
                onChange={(e) => handleInputChange('finSpread', e.target.value)}
                placeholder="e.g., 50.00"
                className={errors.finSpread ? 'error' : ''}
                disabled={isFieldDisabled('finSpread')}
              />
              {errors.finSpread && <FieldError>{errors.finSpread}</FieldError>}
            </FormGroup>
          </LegFormRow>

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="finIndexRate">Index Fixing (%)*</Label>
              <Input
                id="finIndexRate"
                type="number"
                step="0.01"
                value={formData.finIndexRate}
                onChange={(e) => handleInputChange('finIndexRate', e.target.value)}
                placeholder="e.g., 4.35 — accruals use this (no rates feed)"
                className={errors.finIndexRate ? 'error' : ''}
                disabled={isFieldDisabled('finIndexRate')}
              />
              {errors.finIndexRate && <FieldError>{errors.finIndexRate}</FieldError>}
            </FormGroup>
          </LegFormRow>

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="finDayCount">Day Count</Label>
              <Select
                id="finDayCount"
                value={formData.finDayCount}
                onChange={(e) => handleInputChange('finDayCount', e.target.value)}
                disabled={isFieldDisabled('finDayCount')}
              >
                <option value="ACT/360">ACT/360</option>
                <option value="ACT/365">ACT/365</option>
                <option value="ACT/ACT">ACT/ACT</option>
                <option value="30/360">30/360</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="finPayFrequency">Payment Frequency</Label>
              <Select
                id="finPayFrequency"
                value={formData.finPayFrequency}
                onChange={(e) => handleInputChange('finPayFrequency', e.target.value)}
                disabled={isFieldDisabled('finPayFrequency')}
              >
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Semi-Annual">Semi-Annual</option>
                <option value="Annual">Annual</option>
              </Select>
            </FormGroup>
          </LegFormRow>

          <LegFormRowSingle>
            <FormGroup>
              <Label htmlFor="finResetFrequency">Reset Frequency</Label>
              <Select
                id="finResetFrequency"
                value={formData.finResetFrequency}
                onChange={(e) => handleInputChange('finResetFrequency', e.target.value)}
                disabled={isFieldDisabled('finResetFrequency')}
              >
                <option value="Daily">Daily</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Semi-Annual">Semi-Annual</option>
              </Select>
            </FormGroup>
          </LegFormRowSingle>
        </SwapLegPanel>
      </SwapLegsContainer>
    </>
  );
};

export default DerivativesTRSwapTicketScreen;
